import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Header, Button, Field, Groupper, Input, Message, Table, Toolbar, Icon, useFrontier, Dropdown, Checkbox } from 'react-frontier2';
import { CDN_URL, PAGE_PUBLIC } from "../../AdminConfig";
import { useParams, useNavigate, Link } from "react-router-dom";
import { bindClick, bindFormChange, fromUnix } from '@arema/components/Util';
import { DeletedBanner, ErrorHeader, NotFound, SearchEventModal } from '../../components';
import { useTitle } from '@arema/components/Hooks';
import { CatalogEvent } from '@arema/components/Classes';
import { useUser } from '../../AdminHooks';
import MDEditor, { commands } from '@uiw/react-md-editor';
import API, { EventGroupEdit } from "../../API";
import Validator from '@arema/components/Validator';
import UserAccess from '../../UserAccess';

interface EventGroup {
	group_id?: number,
	category_id: number,
	group_name: string,
	subdomain: string,
	published: boolean,
	description: string,
	deleted?: boolean,
	date_deleted?: number,
	user_deleted_name?: string,
	date_publish: number,
}

const styles = {
	placeholderPoster: {
		height: 308,
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		padding: 15,
		backgroundColor: '#f0f0f0',
		borderRadius: 8,
		width: 308
	},
	poster: {
		width: 308,
		height: 308,
		borderRadius: 8
	}
}

export default function EventGroupCreate(props: any){
	var { group_id } = useParams();
	var { setTitle } = useTitle();
	var { hasAccess } = useUser();
	var { Toast } = useFrontier();
	var posterInputRef = useRef<HTMLInputElement>(null);
	var [missingPoster, setMissingPoster] = useState<boolean>(false);
	var [formData, setFormData] = useState<EventGroup>(null)
	var [loadError, setLoadError] = useState<string>(null);
	var [selectedEvents, setSelectedEvents] = useState<(CatalogEvent)[]>([]);
	var [openSearchModal, setOpenSearchModal] = useState<boolean>(false);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [categoryList, setCategoryList] = useState<any[]>(null);
	var IS_CREATE = group_id === 'create';
	var navigate = useNavigate();

	var ACCESS = {
		poster: hasAccess(UserAccess.EVENTS.EDIT_EVENT_GROUP_POSTER),
	}

	useEffect(()=>{
		API.getEventsQL(['event_group_category']).then(res=>{
			if(res.error) return setLoadError(res.message);
			setCategoryList(res.data.event_group_category);
		}).catch(err=>{
			setLoadError('Hubo un error inesperado cargando la información complementaria (LCL-1)');
		})

		if(!IS_CREATE && !Number.isNaN(parseInt(group_id))){
			if(!formData){
				loadData(parseInt(group_id));
			}
		}else if(IS_CREATE){
			setTitle('Crear Evento Padre');
			if(formData) return;
			setFormData({
				group_name: '',
				subdomain: '',
				description: '',
				published: false,
				category_id: null,
				date_publish: null,
			})
		}
	}, []);

	var loadData = async (group_id: number)=>{
		var res = await API.getEventGroup(group_id);
		if(!res.error){
			setFormData(res.data);
			setTitle(`Evento Padre ${res.data.group_id}`);
			if(res.data.events){
				setSelectedEvents(res.data.events);
			}
		}else{
			setLoadError('Hubo un error cargando el evento padre (LCL-3)');
		}
	}

	var uploadPoster = ()=>{
		posterInputRef.current?.click();
	}

	var handlePoster = (ev: React.ChangeEvent<HTMLInputElement>)=>{
		if(!ev.target.files || ev.target.files.length == 0) return;
		if(ev.target.files.length > 1) return Toast.error("Favor de solo seleccionar 1 archivo.");
		var image = ev.target.files[0];
		if(image.size > (2.5 * 1024 * 1024)) return Toast.error('El poster no puede pesar mas de 2.5MB.');
		API.setEventGroupPoster(parseInt(group_id), image).then(res=>{
			if(res.error) return Toast.error(res.message);
			setMissingPoster(false);
			Toast.success('Poster cambiado')
		}).catch(err=>{
			return Toast.error('Hubo un error subiendo el poster del evento padre. (LCL-3)');
		});
	}

	var onSaveEvents = (event: CatalogEvent): boolean=>{
		if(!selectedEvents.some(ev=>ev.event_id === event.event_id)){
			var newEvents = [...selectedEvents];
			newEvents.push(event);
			setSelectedEvents(newEvents);
			return true;
		}
		Toast.error('El evento ya fue seleccionado previamente');
		return false;
	}

	var deleteEvents = (event_id: number)=>{
		return ()=>{
			var newEvents = [...selectedEvents].filter(sev=>sev.event_id !== event_id);
			setSelectedEvents(newEvents);
		}
	}

	var submit = async (setLoading: (b: boolean)=>void)=>{
		var { valid, prompts } = Validator(formData, {
			group_name: [{ rule: 'length', params: [3, 64] }],
			description: [{ rule: 'minLength', params: [3] }],
			subdomain: [{ rule: 'length', params: [3, 16], skipEmpty: true }, { rule: /^(?<s>[a-z0-9]{3,16})$/gi, skipEmpty: true }],
			category_id: [{ rule: 'number', prompt: 'Es obligatorio seleccionar una categoria' }],
		});
		setErrorPrompts(prompts);
		if(!valid) return;
		if(!IS_CREATE){
			formData.group_id = parseInt(group_id);
		}
		setLoading(true);
		var saveGroup: EventGroupEdit = {
			group_name: formData.group_name,
			description: formData.description,
			subdomain: formData.subdomain,
			published: formData.published,
			category_id: formData.category_id,
			date_publish: formData.date_publish,
		}
		if(IS_CREATE){
			API.createEventGroup(saveGroup, selectedEvents.map(d=>d.event_id)).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				Toast.success('Se creó el evento padre');
				navigate(`/parent/${res.data.group_id}`);
			}).catch(err=>{
				setErrorPrompts(['Hubo un error inesperado al crear el evento padre (LCL-10)']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.editEventGroup(formData.group_id, saveGroup, selectedEvents.map(d=>d.event_id)).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				Toast.success('Se ha editado el evento padre.');
			}).catch(err=>{
				setErrorPrompts(['Hubo un error inesperado editando el evento padre (LCL-11)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	var poster_time = useMemo(()=>{
		return new Date().getTime();
	}, []);
	
	if(!IS_CREATE && Number.isNaN(parseInt(group_id))){
		return <NotFound />
	}
	if(loadError){
		return <ErrorHeader error={loadError} />
	}
	if(!formData){
		return <Header loading text='Cargando Evento Padre' />
	}

	var onFormChange = bindFormChange(formData, setFormData);

	return <div className='ar event-group-screen'>
		{ !!formData.deleted ? <DeletedBanner date_deleted={formData.date_deleted} admin_deleter={formData.user_deleted_name} /> : null }
		{(ACCESS.poster && !IS_CREATE) &&
			<Groupper style={{ maxWidth: 350, marginTop: 15, marginBottom: 15 }} title="Poster">
				{missingPoster ? (
					<div style={styles.placeholderPoster}>
						<Header text="Este evento padre no tiene un poster" subtext="Sube un poster para poder publicar el evento" size="small" />
					</div>
				) : (
					<img
						alt='event-group-poster'
						src={`${CDN_URL}/groups/${group_id}/800.webp?t=${poster_time}`}
						style={styles.poster}
						onError={()=>setMissingPoster(true)}
						onLoad={()=>{
							setMissingPoster(false);
						}}
					/>
				)}
				<input type="file" style={{ display: 'none' }} ref={posterInputRef} accept="image/*" onChange={handlePoster} />
				<div className="actions" style={{ textAlign: 'center' }}>
					<Button text="Subir poster" color={'black'} onClick={uploadPoster} />
				</div>
			</Groupper>
		}

		<Groupper title={IS_CREATE ? 'Nuevo Evento Padre' : 'Editar Evento Padre'} style={{ maxWidth: 600, margin: 'auto' }} actions={(
			<Button text="Guardar" color="black" onClick={submit} />
		)}>
			{!IS_CREATE && (
				<Toolbar fitted>
					<Toolbar.Item text='Ver página' iconName='eye' as={Link} to={`${PAGE_PUBLIC}/@${formData.subdomain}`} target='_blank' />
				</Toolbar>
			)}
			<Input label="Nombre" value={formData.group_name} onChange={onFormChange('group_name')} />
			<Dropdown
				label="Categoría"
				loading={!categoryList} 
				value={formData.category_id} 
				onValueChange={onFormChange('category_id')} 
				items={categoryList ? categoryList.map(a=>({
					value: a.category_id,
					text: a.category_name,
				})) : []} 
			/>
			<Input label="Subdominio" value={formData.subdomain} onChange={onFormChange('subdomain')} />
			<Field label='Publicado'>
				<Checkbox
					label={'Evento padre publicado'}
					checked={!!formData.published}
					onChange={onFormChange('published')}
					style={{ marginBottom: 5 }}
				/>
			</Field>
			{!!formData.published && (
				<Input 
					label='Fecha de publicación'
					placeholder="Opcional"
					value={formData.date_publish} 
					calendar={{
						date: formData.date_publish ? fromUnix(formData.date_publish).toJSDate() : null, 
						mode: 'date',
						onSelected: v=>onFormChange('date_publish')(v.getTime()/1000)
					}}
					buttons={!formData.date_publish ? null : (
						<Button icon 
							iconName="remove" 
							style={{ minWidth: 0, paddingRight: 20 }} 
							iconStyle={{ margin: 0 }} 
							onClick={()=>onFormChange('date_publish')(null) } 
						/>)
					}
				/>
			)}

			<div data-color-mode="light">
				<MDEditor
					preview="edit"
					value={(formData.description || '')}
					onChange={onFormChange('description')}
					height={350}
					placeholder="Descripcion del evento padre"
					extraCommands={[
						{
							...commands.codeEdit,
							icon: <Icon name='pencil' style={{ margin: 0, fontSize: 18 }} />
						},
						{
							...commands.codePreview,
							icon: <Icon name='eye' style={{ margin: 0, fontSize: 18 }} />
						},
					]} commands={[
						commands.bold,
						commands.italic,
						commands.strikethrough,
						commands.divider,
						commands.hr,
						commands.quote,
						commands.code,
						commands.divider,
						commands.unorderedListCommand,
						commands.orderedListCommand,
					]} />
			</div>
			<Table
				style={{ marginTop: 15 }}
				className='first'
			>	
				<Table.Head
					title='Eventos'
					data={[['ID', 'Evento', <Icon name='remove' />]]}
					collapsingIndexes={[0, 2]}
					centeredIndexes={[2]}
					titleRight={(
						<Button text='Agregar' color='black' size='tiny' iconName='plus-circle' onClick={bindClick(setOpenSearchModal, true)} />
					)}
				/>
				<Table.Body>
					{selectedEvents.length===0 && (
						<Table.Cell empty row colSpan={100} value={'No hay eventos en este evento padre'} />
					)}
					{selectedEvents.map(a=>(
						<Table.Row key={`SEVT-${a.event_id}`} data={[
							a.event_id,
							a.event_name,
							<Button iconName='remove' color='black' size='tiny' style={{ minWidth: 40 }} onClick={deleteEvents(a.event_id)} />
						]} />
					))}
				</Table.Body>
			</Table>
			<Message type='error' list={errorPrompts} style={{ marginTop: 15 }} />
		</Groupper>

		<SearchEventModal
			open={openSearchModal}
			onClose={bindClick(setOpenSearchModal)}
			title='Buscar Evento'
			onEventSelected={onSaveEvents}
		/>
	</div>
}
